import { Button } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { ClosingTabProps } from "features/closings/tabs/ClosingTabProps";
import { aggFuncForFeesBreakdown } from "features/fees/posted/PostedFeeChargeUtils";
import {
  getClosingStatsRetrieveQueryKey,
  useClosingRetrieveSuspense,
} from "gen/endpoints/closing/closing";
import {
  getFundFamilyEventRetrieveQueryKey,
  useFeeOffsetsTrueUpBreakdownTableDataRetrieveSuspense,
  useFeeTrueUpBreakdownForEventTableDataRetrieveSuspense,
  useFeeTrueUpEventCreate,
  useFundFamilyEventRetrieve,
  useFundFamilySubEventList,
} from "gen/endpoints/fund-family-event/fund-family-event";
import { FundFamilyEventStateMachineFieldsEnum } from "gen/model/fundFamilyEventStateMachineFieldsEnum";
import { FundFamilySubEventTypeEnum } from "gen/model/fundFamilySubEventTypeEnum";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { useToast } from "shared/components/toast/useToast";
import { useCeleryWorkflowPoll } from "shared/hooks/useCeleryWorkflowPoll";

export const ClosingTabFeeTrueUps: React.FC = () => {
  const { showToast, showErrorToast } = useToast();
  const { t } = useTranslation(["common", "equalization", "fees", "closing"]);

  const { closingId, fundFamilyId } = useOutletContext<ClosingTabProps>();
  const { data: closingData } = useClosingRetrieveSuspense(
    fundFamilyId,
    closingId,
  );

  const fundFamilyEventId = closingData.fund_family_event_id;
  const { data: fundFamilyEventData } = useFundFamilyEventRetrieve(
    fundFamilyId,
    fundFamilyEventId,
    {
      query: {
        enabled: !!fundFamilyEventId,
      },
    },
  );

  const { data: fundFamilySubEvents, refetch: refetchFundFamilySubEventList } =
    useFundFamilySubEventList(fundFamilyId, fundFamilyEventId);
  const filteredSubEvents = fundFamilySubEvents?.filter(
    (subEvent) => subEvent.type === FundFamilySubEventTypeEnum.true_up_fee,
  );
  const workflowIds = filteredSubEvents?.map(
    (subEvent) => subEvent.celery_workflow_id as string | undefined,
  );

  const isDraft =
    fundFamilyEventData?.state === FundFamilyEventStateMachineFieldsEnum.draft;

  const queryClient = useQueryClient();

  const {
    data: catchUpBreakdownTableData,
    refetch: refetchCatchUpBreakdownTableData,
  } = useFeeTrueUpBreakdownForEventTableDataRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );
  const {
    data: offsetsTrueUpBreakdownTableData,
    refetch: refetchOffsetsTrueUpBreakdownTableData,
  } = useFeeOffsetsTrueUpBreakdownTableDataRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );

  const {
    mutateAsync: createFeeTrueUpEvent,
    isPending: isPendingCreateFeeTrueUpEvent,
  } = useFeeTrueUpEventCreate();

  const refetchTables = async () => {
    await Promise.all([
      refetchCatchUpBreakdownTableData(),
      refetchOffsetsTrueUpBreakdownTableData(),
      queryClient.invalidateQueries({
        queryKey: getClosingStatsRetrieveQueryKey(fundFamilyId, closingId),
      }),
    ]);
  };

  const renderToasts = (isError: boolean) => {
    if (isError) {
      showToast({
        message: t("fees:feeTrueUp.celeryToasts.error"),
        variant: "error",
      });
    } else {
      showToast({
        message: t("fees:feeTrueUp.celeryToasts.success"),
        variant: "success",
      });
    }
  };

  useCeleryWorkflowPoll(
    workflowIds?.filter((id) => id !== undefined) as string[],
    renderToasts,
    refetchTables,
    refetchTables,
  );

  const refreshFeeTrueUp = async () => {
    try {
      await createFeeTrueUpEvent({
        fundFamilyId,
        fundFamilyEventId,
      });
      await refetchFundFamilySubEventList();
      await queryClient.invalidateQueries({
        queryKey: getFundFamilyEventRetrieveQueryKey(
          fundFamilyId,
          fundFamilyEventId,
        ),
      });
    } catch (err) {
      showErrorToast(err);
    }
  };

  return (
    <PageTabBody isFullHeight={false}>
      <TableBox>
        <TableBoxHeader>
          <TableBoxTitle>{t("closing:managementFeeCatchUp")}</TableBoxTitle>
          <DataTableBoxActions>
            {isDraft && catchUpBreakdownTableData.rows.length && (
              <Button
                colorScheme="tertiary"
                size="xs"
                onClick={() => refreshFeeTrueUp()}
                isLoading={isPendingCreateFeeTrueUpEvent}
              >
                {t("common:refresh")}
              </Button>
            )}
          </DataTableBoxActions>
        </TableBoxHeader>
        <TableBoxBody>
          <DataTable
            data={catchUpBreakdownTableData}
            aggFunc={aggFuncForFeesBreakdown}
            emptyStateProps={
              isDraft
                ? {
                    title: t("closing:trueUpEmptyTitle"),
                    children: (
                      <Button
                        size="sm"
                        onClick={() => refreshFeeTrueUp()}
                        isLoading={isPendingCreateFeeTrueUpEvent}
                      >
                        {t("common:refresh")}
                      </Button>
                    ),
                  }
                : undefined
            }
          />
        </TableBoxBody>
      </TableBox>
      <TableBox>
        <TableBoxHeader>
          <TableBoxTitle>{t("closing:equalizedOffsets")}</TableBoxTitle>
          <DataTableBoxActions />
        </TableBoxHeader>
        <TableBoxBody>
          <DataTable data={offsetsTrueUpBreakdownTableData} />
        </TableBoxBody>
      </TableBox>
    </PageTabBody>
  );
};
